import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import {EquityDividendWithAccountsJoin} from "@/integrations/supabase/equity.models.ts";
import {convertToAccountCurrency} from "@lib/currencyConversion/accountCurrency.ts";

const DEFAULT_PAGE_SIZE = 100;

class EquityDividendService extends BaseService {

    /**
     * Records a new equity dividend payment through the record_equity_dividend RPC, which credits the
     * target account, files the income under "Other" / "Dividends/Equity" and records the dividend
     * linked to its transaction, in one database transaction.
     *
     * The amount is converted into the target account's currency first; no rates are fetched when the
     * currencies match.
     */
    async createEquityDividend(request: EquityDividendCreateRequest): Promise<{ dividend_id: string; transaction_id: string }> {
        const { userId, targetAccountId, amount, currency } = request;
        try {
            const { convertedAmount } = await convertToAccountCurrency(userId, targetAccountId, amount, 0, currency);

            const { data, error } = await supabase.rpc('record_equity_dividend', {
                p_user_id: userId,
                p_equity_security_id: request.equitySecurityId,
                p_target_account_id: targetAccountId,
                p_amount: amount,
                p_currency: currency,
                p_payment_date: request.paymentDate,
                p_notes: request.notes ?? null,
                p_credit_amount: convertedAmount,
            });
            if (error) throw error;

            return data as { dividend_id: string; transaction_id: string };
        } catch (error) {
            await this.handleError(error);
            throw error;
        }
    }

    /**
     * Fetches equity dividends for a given equity security.
     * @param {EquityDividendFetchRequest} request
     */
    async fetchDividendsForEquity(request: EquityDividendFetchRequest): Promise<{ dividends: EquityDividendWithAccountsJoin[], totalCount: number }> {
        const { userId, equitySecurityId, page, pageSize } = request;
        const pageSizeToUse = pageSize ?? DEFAULT_PAGE_SIZE;
        const from = (page - 1) * pageSizeToUse;
        const to = from + pageSizeToUse - 1;

        const { data, error, count } = await supabase
            .from('equity_dividends')
            .select('*, accounts!target_account_id(name, currency)', { count: 'exact' })
            .eq('equity_security_id', equitySecurityId)
            .eq("user_id", userId)
            .order('payment_date', { ascending: false })
            .order('created_at', { ascending: false })
            .range(from, to);

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return {
            dividends: data,
            totalCount: count ?? 0,
        };
    }
}

export default new EquityDividendService();
